import { useEffect, useRef, useState } from 'react';
import { Course, CourseType } from '../../apis/api-client';
import { coursesClient } from '../../apis/client-instances';
import { Autocomplete, Box, Chip, CircularProgress, TextField, Typography } from '../ui';

// Type-coded chip palette for the option dropdown. Same hues used on
// the admin builder + sidebar so a "Learning Path" row reads the same
// way no matter where the user encounters it.
function typeChipMeta(type: CourseType | undefined): { label: string; bgcolor: string; color: string } {
  switch (type) {
    case CourseType.LearningPath:
      return { label: 'Path', bgcolor: 'rgba(106,27,154,0.10)', color: '#6a1b9a' };
    case CourseType.External:
      return { label: 'Event', bgcolor: 'rgba(46,125,50,0.10)', color: '#2e7d32' };
    default:
      return { label: 'Course', bgcolor: 'rgba(21,101,192,0.10)', color: '#1565c0' };
  }
}

const SEARCH_PAGE_SIZE = 25;
const DEBOUNCE_MS = 250;
const DEFAULT_MIN_WIDTH = 240;

export interface CourseAutocompleteProps {
  value: Course | Course[] | null;
  onChange: (value: Course | Course[] | null) => void;
  multiple?: boolean;
  label?: string;
  placeholder?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  sx?: any;
}

/**
 * Lazy-loaded course picker. Server-side search via /api/Courses with name + paging
 * so we never bulk-load every course in the account.
 *
 * The caller owns the selected Course objects. Initial hydration from stored IDs is
 * not handled here — fetch with `getCourseById` and hand the resulting Course in.
 */
export default function CourseAutocomplete({
  value,
  onChange,
  multiple = false,
  label,
  placeholder,
  size = 'small',
  fullWidth = false,
  disabled = false,
  required = false,
  error = false,
  helperText,
  sx,
}: CourseAutocompleteProps) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<Course[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);

  const reqIdRef = useRef(0);

  useEffect(() => {
    if (!open) return;
    const id = ++reqIdRef.current;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const results = await coursesClient.getCourses(
          undefined,                       // id
          inputValue || undefined,         // name
          undefined,                       // accountId
          undefined,                       // workspaceId
          undefined,                       // isDraft
          undefined,                       // tagIds
          undefined,                       // publishedDateFrom
          undefined,                       // publishedDateTo
          undefined,                       // myCourses
          undefined,                       // isAssigned
          undefined,                       // courseType
          0,                               // offset
          SEARCH_PAGE_SIZE,                // limit
          undefined,                       // securityWorkspaceId
          undefined                        // isPublicOnly
        );
        if (id !== reqIdRef.current) return;
        setOptions(results ?? []);
      } catch {
        if (id !== reqIdRef.current) return;
        setOptions([]);
      } finally {
        if (id === reqIdRef.current) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [inputValue, open]);

  const hasSelection = multiple
    ? Array.isArray(value) && value.length > 0
    : value != null;

  return (
    <Autocomplete<Course, boolean, false, false>
      multiple={multiple}
      size={size}
      fullWidth={fullWidth}
      disabled={disabled}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={value as any}
      onChange={(_e, newValue) => onChange(newValue as any)}
      inputValue={inputValue}
      onInputChange={(_e, val) => setInputValue(val)}
      options={options}
      loading={loading}
      filterOptions={(x) => x}
      getOptionLabel={(option) => option?.title ?? ''}
      isOptionEqualToValue={(opt, val) => opt.id === (val as Course)?.id}
      noOptionsText={inputValue ? 'No content matches' : 'Type to search courses, paths, or events'}
      renderOption={(props, option) => {
        const meta = typeChipMeta(option.courseType);
        return (
          <Box component="li" {...props} key={option.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {option.title}
            </Typography>
            <Chip
              label={meta.label}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                fontWeight: 700,
                letterSpacing: '0.04em',
                bgcolor: meta.bgcolor,
                color: meta.color,
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
          </Box>
        );
      }}
      renderTags={(items, getTagProps) =>
        items.map((option, index) => {
          const meta = typeChipMeta(option.courseType);
          return (
            <Chip
              label={
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  <Box
                    component="span"
                    sx={{
                      fontSize: '0.6rem',
                      fontWeight: 700,
                      letterSpacing: '0.04em',
                      bgcolor: meta.bgcolor,
                      color: meta.color,
                      px: 0.6,
                      py: 0.05,
                      borderRadius: 0.5,
                    }}
                  >
                    {meta.label.toUpperCase()}
                  </Box>
                  {option.title}
                </Box>
              }
              size="small"
              {...getTagProps({ index })}
              key={option.id}
            />
          );
        })
      }
      sx={{
        minWidth: DEFAULT_MIN_WIDTH,
        '& .MuiAutocomplete-clearIndicator': {
          visibility: hasSelection ? 'visible' : 'hidden',
        },
        ...sx,
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
